import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Globe, Plane, GraduationCap, DollarSign, CheckCircle, ArrowRight, Calendar, FileText, Award, X, ChevronLeft, ChevronRight, Download } from 'lucide-react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { mockAbroadColleges, mockAbroadLeads, type AbroadCollege } from '@/data/admin-mock-data'

export default function ForeignEducationPage() {
    const [selectedCollege, setSelectedCollege] = useState<AbroadCollege | null>(null)
    const [enrollModalOpen, setEnrollModalOpen] = useState(false)
    const [enrollForm, setEnrollForm] = useState({ name: '', email: '', phone: '', message: '' })
    const [activeImageIndex, setActiveImageIndex] = useState(0)

    const colleges = mockAbroadColleges.filter(c => c.isActive)

    const openDetails = (c: AbroadCollege) => {
        setSelectedCollege(c)
        setActiveImageIndex(0)
    }

    const nextImage = () => {
        if (!selectedCollege) return
        setActiveImageIndex(prev => (prev + 1) % (selectedCollege.galleryImages?.length || 1))
    }

    const prevImage = () => {
        if (!selectedCollege) return
        setActiveImageIndex(prev => (prev - 1 + (selectedCollege.galleryImages?.length || 1)) % (selectedCollege.galleryImages?.length || 1))
    }

    const handleEnrollSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        // Determine college context
        const collegeId = selectedCollege?.id || 'general'
        const collegeName = selectedCollege?.name || 'General Inquiry'

        // In a real app, this would be an API call
        const newLead = {
            id: `l-${Date.now()}`,
            studentName: enrollForm.name,
            email: enrollForm.email,
            phone: enrollForm.phone,
            collegeId,
            collegeName,
            status: 'new',
            assignedMentorId: null,
            assignedMentorName: null,
            message: enrollForm.message,
            createdAt: new Date().toISOString().split('T')[0]
        }

        // Push to mock data (will reset on reload)
        // @ts-ignore
        mockAbroadLeads.push(newLead)

        alert('Application submitted successfully! Our counsellor will contact you shortly.')
        setEnrollModalOpen(false)
        setEnrollForm({ name: '', email: '', phone: '', message: '' })
    }

    const admissionProcess = [
        {
            step: '1',
            title: 'Free Counselling',
            description: 'Get personalized guidance from our expert counsellors',
            icon: FileText,
        },
        {
            step: '2',
            title: 'University Selection',
            description: 'Choose the best university based on your preferences and budget',
            icon: GraduationCap,
        },
        {
            step: '3',
            title: 'Document Preparation',
            description: 'We help you prepare and verify all required documents',
            icon: CheckCircle,
        },
        {
            step: '4',
            title: 'Application Submission',
            description: 'Submit applications to selected universities',
            icon: FileText,
        },
        {
            step: '5',
            title: 'Admission Letter',
            description: 'Receive admission confirmation from university',
            icon: Award,
        },
        {
            step: '6',
            title: 'Visa Processing',
            description: 'Complete visa formalities with our assistance',
            icon: Plane,
        },
    ]

    const eligibility = [
        'Passed 10+2 with Physics, Chemistry, Biology',
        'Minimum 50% aggregate (40% for SC/ST/OBC)',
        'NEET qualification mandatory',
        'Age: 17 years minimum at admission',
    ]

    return (
        <div className="min-h-screen">
            {/* Hero Section */}
            <section className="relative pt-32 pb-24 overflow-hidden">
                <div className="container mx-auto px-4 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center max-w-4xl mx-auto"
                    >
                        <span className="inline-block px-4 py-2 rounded-full glass-dark text-sm font-medium text-primary-blue border border-primary-blue/30 mb-6">
                            Study MBBS Abroad
                        </span>
                        <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold mb-6">
                            <span className="text-theme-primary">Your Gateway to</span>
                            <span className="block mt-2 gradient-text">Global Medical Education</span>
                        </h1>
                        <p className="text-xl text-theme-secondary leading-relaxed mb-8">
                            Expert counselling for MBBS admission in top international universities
                        </p>
                        <button onClick={() => { setSelectedCollege(null); setEnrollModalOpen(true) }} className="px-8 py-4 bg-primary-blue hover:bg-blue-600 text-white font-semibold rounded-lg glow-primary transition-all duration-300 inline-flex items-center gap-2">
                            Book Free Counselling
                            <ArrowRight className="h-5 w-5" />
                        </button>
                    </motion.div>
                </div>
            </section>

            {/* Featured Colleges */}
            <section className="py-24">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl md:text-5xl font-display font-bold text-theme-primary mb-4">
                            Featured <span className="gradient-text">Colleges</span>
                        </h2>
                        <p className="text-lg text-theme-secondary">
                            Top medical universities with proven track record
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {colleges.map((college, index) => (
                            <motion.div
                                key={college.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <GlassmorphicCard className="overflow-hidden hover:scale-105 transition-all duration-300 h-full flex flex-col">
                                    <div className="relative h-48 overflow-hidden group cursor-pointer" onClick={() => openDetails(college)}>
                                        <img
                                            src={college.image}
                                            alt={college.name}
                                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                        />
                                        <div className="absolute top-3 left-3">
                                            <span className="px-3 py-1 bg-primary-blue text-white text-sm font-medium rounded-full">
                                                {college.country}
                                            </span>
                                        </div>
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <span className="text-white font-semibold border border-white px-4 py-2 rounded-lg">View Details</span>
                                        </div>
                                    </div>
                                    <div className="p-6 flex flex-col flex-1">
                                        <h3 className="text-xl font-display font-semibold text-theme-primary mb-3">
                                            {college.name}
                                        </h3>
                                        <div className="space-y-2 mb-4 flex-1">
                                            <div className="flex items-center gap-2 text-theme-secondary">
                                                <Award className="h-4 w-4 text-primary-blue" />
                                                <span className="text-sm">{college.ranking}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-theme-secondary">
                                                <DollarSign className="h-4 w-4 text-primary-blue" />
                                                <span className="text-sm">{college.fees}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-theme-secondary">
                                                <Calendar className="h-4 w-4 text-primary-blue" />
                                                <span className="text-sm">Intake: {college.intake}</span>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <button onClick={() => openDetails(college)} className="flex-1 py-3 border border-primary-blue text-primary-blue hover:bg-primary-blue hover:text-white font-medium rounded-lg transition-all duration-300 text-sm">
                                                Details
                                            </button>
                                            <button onClick={() => { setSelectedCollege(college); setEnrollModalOpen(true) }} className="flex-1 py-3 bg-primary-blue hover:bg-blue-600 text-white font-medium rounded-lg transition-all duration-300 text-sm">
                                                Enroll
                                            </button>
                                        </div>
                                    </div>
                                </GlassmorphicCard>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* College Details Modal */}
            <AnimatePresence>
                {selectedCollege && !enrollModalOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                        onClick={() => setSelectedCollege(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="w-full max-w-4xl max-h-[90vh] overflow-y-auto"
                            onClick={e => e.stopPropagation()}
                        >
                            <GlassmorphicCard className="overflow-hidden">
                                <div className="relative h-64 md:h-80 bg-slate-900">
                                    <img
                                        src={selectedCollege.galleryImages && selectedCollege.galleryImages.length > 0
                                            ? selectedCollege.galleryImages[activeImageIndex]
                                            : selectedCollege.image}
                                        alt={selectedCollege.name}
                                        className="w-full h-full object-cover"
                                    />

                                    {/* Carousel Controls */}
                                    {selectedCollege.galleryImages && selectedCollege.galleryImages.length > 1 && (
                                        <>
                                            <button onClick={prevImage} className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors">
                                                <ChevronLeft className="h-6 w-6" />
                                            </button>
                                            <button onClick={nextImage} className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors">
                                                <ChevronRight className="h-6 w-6" />
                                            </button>
                                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                                                {selectedCollege.galleryImages.map((_, idx) => (
                                                    <div
                                                        key={idx}
                                                        className={`h-2 w-2 rounded-full transition-all ${idx === activeImageIndex ? 'bg-white w-4' : 'bg-white/50'}`}
                                                    />
                                                ))}
                                            </div>
                                        </>
                                    )}

                                    <button onClick={() => setSelectedCollege(null)} className="absolute top-4 right-4 p-2 rounded-full bg-black/40 text-white hover:bg-red-500 transition-colors">
                                        <X className="h-5 w-5" />
                                    </button>
                                    <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                                        <span className="px-3 py-1 bg-primary-blue text-white text-sm font-medium rounded-full mb-2 inline-block">
                                            {selectedCollege.country}
                                        </span>
                                        <h2 className="text-3xl font-bold text-white">{selectedCollege.name}</h2>
                                    </div>
                                </div>

                                <div className="p-6 md:p-8">
                                    <div className="grid md:grid-cols-2 gap-8 mb-8">
                                        <div className="space-y-4">
                                            <h3 className="text-xl font-semibold text-theme-primary border-b border-primary-blue/20 pb-2">Key Highlights</h3>
                                            <ul className="space-y-3">
                                                <li className="flex justify-between text-theme-secondary">
                                                    <span>Ranking:</span>
                                                    <span className="font-medium text-theme-primary">{selectedCollege.ranking}</span>
                                                </li>
                                                <li className="flex justify-between text-theme-secondary">
                                                    <span>Intake:</span>
                                                    <span className="font-medium text-theme-primary">{selectedCollege.intake}</span>
                                                </li>
                                                <li className="flex justify-between text-theme-secondary">
                                                    <span>Medium:</span>
                                                    <span className="font-medium text-theme-primary">{selectedCollege.teachingMedium || 'English'}</span>
                                                </li>
                                                <li className="flex justify-between text-theme-secondary">
                                                    <span>NMC Status:</span>
                                                    <span className={`font-medium ${selectedCollege.nmcStatus === 'Approved' ? 'text-green-600' : 'text-amber-600'}`}>
                                                        {selectedCollege.nmcStatus || 'Pending'}
                                                    </span>
                                                </li>
                                            </ul>
                                        </div>
                                        <div className="space-y-4">
                                            <h3 className="text-xl font-semibold text-theme-primary border-b border-primary-blue/20 pb-2">Fee Structure</h3>
                                            <ul className="space-y-3">
                                                <li className="flex justify-between text-theme-secondary">
                                                    <span>Tuition Fees:</span>
                                                    <span className="font-medium text-theme-primary">{selectedCollege.fees}</span>
                                                </li>
                                                <li className="flex justify-between text-theme-secondary">
                                                    <span>Hostel Fees:</span>
                                                    <span className="font-medium text-theme-primary">{selectedCollege.hostelFees || 'N/A'}</span>
                                                </li>
                                            </ul>

                                            {selectedCollege.eligibility && (
                                                <div className="mt-4 p-4 bg-primary-blue/5 rounded-lg border border-primary-blue/10">
                                                    <p className="text-sm font-medium text-primary-blue mb-1">Eligibility</p>
                                                    <p className="text-sm text-theme-secondary">{selectedCollege.eligibility}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex flex-col sm:flex-row gap-4">
                                        <button onClick={() => setEnrollModalOpen(true)} className="flex-1 px-8 py-4 bg-primary-blue hover:bg-blue-600 text-white font-semibold rounded-xl glow-primary transition-all duration-300">
                                            Enroll Now
                                        </button>
                                        <a
                                            href={selectedCollege.brochureUrl || '#'}
                                            download
                                            className="flex-1 px-8 py-4 border border-theme-border hover:bg-slate-100 dark:hover:bg-slate-800 text-theme-primary font-semibold rounded-xl transition-all duration-300 flex items-center justify-center gap-2"
                                            onClick={(e) => { if (!selectedCollege.brochureUrl) { e.preventDefault(); alert('Brochure not available yet.'); } }}
                                        >
                                            <Download className="h-5 w-5" />
                                            Download Brochure
                                        </a>
                                    </div>
                                </div>
                            </GlassmorphicCard>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Enroll Modal */}
            <AnimatePresence>
                {enrollModalOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                        onClick={() => setEnrollModalOpen(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="w-full max-w-md"
                            onClick={e => e.stopPropagation()}
                        >
                            <GlassmorphicCard className="p-6 relative">
                                <button onClick={() => setEnrollModalOpen(false)} className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                                    <X className="h-5 w-5 text-theme-secondary" />
                                </button>

                                <h3 className="text-2xl font-bold text-theme-primary mb-2">Apply for Admission</h3>
                                <p className="text-theme-secondary mb-6 text-sm">
                                    {selectedCollege
                                        ? `Fill in your details for ${selectedCollege.name}`
                                        : 'Fill in your details for free counselling'}
                                </p>

                                <form onSubmit={handleEnrollSubmit} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-1">Full Name</label>
                                        <input
                                            type="text"
                                            required
                                            value={enrollForm.name}
                                            onChange={e => setEnrollForm(f => ({ ...f, name: e.target.value }))}
                                            className="w-full px-4 py-2.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary focus:outline-none focus:ring-2 focus:ring-primary-blue/50"
                                            placeholder="Enter your name"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-1">Email Address</label>
                                        <input
                                            type="email"
                                            required
                                            value={enrollForm.email}
                                            onChange={e => setEnrollForm(f => ({ ...f, email: e.target.value }))}
                                            className="w-full px-4 py-2.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary focus:outline-none focus:ring-2 focus:ring-primary-blue/50"
                                            placeholder="Enter your email"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-1">Phone Number</label>
                                        <input
                                            type="tel"
                                            required
                                            value={enrollForm.phone}
                                            onChange={e => setEnrollForm(f => ({ ...f, phone: e.target.value }))}
                                            className="w-full px-4 py-2.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary focus:outline-none focus:ring-2 focus:ring-primary-blue/50"
                                            placeholder="Enter your phone"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-1">Message (Optional)</label>
                                        <textarea
                                            rows={3}
                                            value={enrollForm.message}
                                            onChange={e => setEnrollForm(f => ({ ...f, message: e.target.value }))}
                                            className="w-full px-4 py-2.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary focus:outline-none focus:ring-2 focus:ring-primary-blue/50"
                                            placeholder="Any questions?"
                                        />
                                    </div>
                                    <button type="submit" className="w-full py-3 bg-primary-blue hover:bg-blue-600 text-white font-semibold rounded-xl transition-all duration-300">
                                        Submit Application
                                    </button>
                                </form>
                            </GlassmorphicCard>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Admission Process */}
            <section className="py-24">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl md:text-5xl font-display font-bold text-theme-primary mb-4">
                            Admission <span className="gradient-text">Process</span>
                        </h2>
                        <p className="text-lg text-theme-secondary">
                            Simple and transparent admission process
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                        {admissionProcess.map((item, index) => {
                            const Icon = item.icon
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    <GlassmorphicCard className="p-6 hover:scale-105 transition-all duration-300 h-full">
                                        <div className="flex items-start gap-4 mb-4">
                                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-blue to-accent-gold flex items-center justify-center flex-shrink-0">
                                                <span className="text-white font-bold">{item.step}</span>
                                            </div>
                                            <Icon className="h-8 w-8 text-primary-blue" />
                                        </div>
                                        <h3 className="text-xl font-display font-semibold text-theme-primary mb-2">
                                            {item.title}
                                        </h3>
                                        <p className="text-theme-secondary">{item.description}</p>
                                    </GlassmorphicCard>
                                </motion.div>
                            )
                        })}
                    </div>
                </div>
            </section>

            {/* Eligibility Criteria */}
            <section className="py-24">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="text-center mb-12"
                        >
                            <h2 className="text-4xl md:text-5xl font-display font-bold text-theme-primary mb-4">
                                Eligibility <span className="gradient-text">Criteria</span>
                            </h2>
                        </motion.div>

                        <GlassmorphicCard className="p-8">
                            <div className="space-y-4">
                                {eligibility.map((criterion, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: index * 0.1 }}
                                        className="flex items-start gap-3"
                                    >
                                        <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                                        <span className="text-lg text-theme-secondary">{criterion}</span>
                                    </motion.div>
                                ))}
                            </div>
                        </GlassmorphicCard>
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="py-24">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl md:text-5xl font-display font-bold text-theme-primary mb-4">
                            Why Choose <span className="gradient-text">Future Founder</span>
                        </h2>
                    </motion.div>

                    <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                        {[
                            {
                                icon: Award,
                                title: 'Expert Counsellors',
                                description: 'Dedicated counsellors with years of experience in international admissions',
                            },
                            {
                                icon: Globe,
                                title: 'Wide Network',
                                description: 'Partnerships with 200+ universities across multiple countries',
                            },
                            {
                                icon: CheckCircle,
                                title: 'End-to-End Support',
                                description: 'Complete assistance from counselling to visa and beyond',
                            },
                        ].map((item, index) => {
                            const Icon = item.icon
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    <GlassmorphicCard className="p-6 text-center hover:scale-105 transition-all duration-300 h-full">
                                        <div className="w-16 h-16 rounded-full bg-primary-blue/20 flex items-center justify-center mx-auto mb-4">
                                            <Icon className="h-8 w-8 text-primary-blue" />
                                        </div>
                                        <h3 className="text-xl font-display font-semibold text-theme-primary mb-3">
                                            {item.title}
                                        </h3>
                                        <p className="text-theme-secondary">{item.description}</p>
                                    </GlassmorphicCard>
                                </motion.div>
                            )
                        })}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24">
                <div className="container mx-auto px-4">
                    <GlassmorphicCard variant="elevated" className="p-12 text-center max-w-4xl mx-auto">
                        <Plane className="h-16 w-16 text-primary-blue mx-auto mb-6" />
                        <h2 className="text-3xl md:text-4xl font-display font-bold text-theme-primary mb-4">
                            Start Your MBBS Journey Abroad
                        </h2>
                        <p className="text-lg text-theme-secondary mb-8 max-w-2xl mx-auto">
                            Book a free counselling session with our experts and get personalized guidance for your medical education abroad
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button onClick={() => { setSelectedCollege(null); setEnrollModalOpen(true) }} className="px-8 py-4 bg-primary-blue hover:bg-blue-600 text-white font-semibold rounded-lg glow-primary transition-all duration-300 inline-flex items-center justify-center gap-2">
                                Book Free Counselling
                                <ArrowRight className="h-5 w-5" />
                            </button>
                            <button className="px-8 py-4 border-2 border-primary-blue text-primary-blue hover:bg-primary-blue hover:text-white font-semibold rounded-lg transition-all duration-300">
                                Download Brochure
                            </button>
                        </div>
                    </GlassmorphicCard>
                </div>
            </section>
        </div>
    )
}
